<template>
  <page-template :show-loader="false">
    <template #pageHeadline>
      {{ $t('versionControl.page.pageHeadline') }}
    </template>
    <template #secondLevelHeaderRight>
      <rbz-form-button
        v-if="branchNames.length > 1"
        :label="$t('common.operations.fork.label')"
        variant="tertiary"
        icon="IconFork"
        :disabled="isDownloadLoading"
        @button-click="openForkBranchModal"
        data-qa="fork-branch-button"
      />
      <rbz-form-button
        :label="$t('common.operations.download.label')"
        variant="tertiary"
        icon="IconDownload"
        :classes="isDownloadLoading ? 'is-loading' : ''"
        @button-click="downloadBranch"
        data-qa="download-branch-button"
      />
      <rbz-form-button
        v-if="branchNames.length > 1"
        :label="$t('common.operations.delete.label')"
        variant="tertiary"
        icon="IconDelete"
        @button-click="openDeleteBranchModal"
        :disabled="isDownloadLoading || isSelectedBranchProtected"
        :tooltip="deleteTooltip"
        data-qa="delete-branch-button"
      />
    </template>
    <template #pageContent v-if="selectedBranch">
      <git-history
        :collapsible-card="false"
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.singular} ${selectedBranch}]`"
        :is-collapsed="false"
        show-filter-button
      />
      <rbz-modal-dialog
        :show="showForkBranchModal"
        :title="$t('common.operations.fork.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.fork.modal.confirm')"
        :primary-btn-disabled="!isForkBranchNameValid"
        @onPrimaryBtnClick="forkBranch"
        :secondary-btn-title="$t('common.operations.fork.modal.cancel')"
        @onSecondaryBtnClick="showForkBranchModal = false"
        @closeModal="showForkBranchModal = false"
        size="small"
      >
        <template #content>
          <div class="rbz-field">
            <rbz-form-input
              name="new-branch-name-input"
              data-qa="new-branch-name-input"
              :label="$t('common.operations.fork.modal.inputLabel')"
              :placeholder="$t('common.operations.fork.modal.inputLabel')"
              :is-invalid="!isForkBranchNameValid"
              :error-message-text="forkBranchNameError"
              v-model:inputValue="forkBranchName"
            />
          </div>
        </template>
      </rbz-modal-dialog>
      <rbz-modal-dialog
        :show="showDeleteBranchModal"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        :primary-btn-disabled="!isDeleteBranchNameValid"
        @onPrimaryBtnClick="deleteBranch"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteBranchModal = false"
        @closeModal="showDeleteBranchModal = false"
        size="small"
      >
        <template #content>
          <div class="rbz-field">
            <rbz-form-input
              name="delete-branch-name-input"
              data-qa="delete-branch-name-input"
              :label="$t('versionControl.page.deleteConfirm')"
              :placeholder="$t('versionControl.page.deleteConfirmPlaceholder')"
              v-model:inputValue="deleteBranchName"
            />
          </div>
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import RequestsUtils from '@/helpers/RequestsUtils';
import GitHistory from '@/components/GitHistory.vue';
import { defineComponent } from 'vue';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import { Branch } from '@/types';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import Utils from 'ui-component-library/helpers/Utils';
import { AxiosResponse } from 'axios';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';

export default defineComponent({
  name: 'VersionControl',
  components: {
    PageTemplate,
    RbzFormInput,
    RbzModalDialog,
    RbzFormButton,
    GitHistory,
  },

  data() {
    return {
      branches: null as Branch[],

      // Fork
      showForkBranchModal: false,
      forkBranchName: '',

      // Download
      isDownloadLoading: false,

      // Delete
      showDeleteBranchModal: false,
      deleteBranchName: '',
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if (val && val !== oldVal) {
          this.showForkBranchModal = false;
          this.showDeleteBranchModal = false;
        }
      },
    },
  },

  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    entityName() {
      return {
        plural: this.$t('versionControl.plural'),
        singular: this.$t('versionControl.singular'),
      };
    },

    gitAPIPath(): string {
      return `conf/configs/${this.selectedBranch}/versions`;
    },

    branchNames(): string[] {
      return this.branches?.length ? _.sortBy(_.map(this.branches, 'id')) : [];
    },

    forkBranchNameError(): string {
      const newName = this.forkBranchName.trim();
      const isBranchNameEmpty = newName === '';
      if (isBranchNameEmpty) {
        return this.$t('common.operations.fork.modal.errors.empty');
      }
      const isBranchNameContainsSpaces = newName.includes(' ');
      if (isBranchNameContainsSpaces) {
        return this.$t('common.operations.fork.modal.errors.spaces');
      }
      const isBranchNameDuplicate = this.branches?.find((item) => {
        return item.id === newName;
      });
      if (isBranchNameDuplicate) {
        return this.$t('common.operations.fork.modal.errors.duplicate');
      }
      return null;
    },

    isForkBranchNameValid(): boolean {
      return !this.forkBranchNameError;
    },

    isDeleteBranchNameValid(): boolean {
      const inputName = this.deleteBranchName.trim();
      return inputName === this.selectedBranch;
    },

    isSelectedBranchProtected(): boolean {
      return ['prod', 'stage'].includes(this.selectedBranch?.toLowerCase());
    },

    deleteTooltip(): string {
      if (this.isSelectedBranchProtected) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      return '';
    },
  },

  methods: {
    openForkBranchModal() {
      this.showForkBranchModal = true;
      this.forkBranchName = '';
    },

    openDeleteBranchModal() {
      this.showDeleteBranchModal = true;
      this.deleteBranchName = '';
    },

    forkBranch() {
      const successMessage = this.$t('common.operations.fork.messages.success', {
        ...this.entityName,
        ...{ source: this.selectedBranch, target: this.forkBranchName },
      });
      const failureMessage = this.$t('common.operations.fork.messages.failure', {
        ...this.entityName,
        ...{ source: this.selectedBranch, target: this.forkBranchName },
      });
      RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url: `conf/configs/${this.selectedBranch}/clone/${this.forkBranchName}`,
        data: {
          id: 'string',
          description: 'string',
        },
        successMessage,
        failureMessage,
      })
        .then(async () => {
          this.branches = await this.branchesStore.loadBranches();
          await this.branchesStore.setSelectedBranch(this.forkBranchName);
        })
        .finally(() => {
          this.showForkBranchModal = false;
        });
    },

    async downloadBranch() {
      this.isDownloadLoading = true;
      RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/configs/${this.selectedBranch}`,
      })
        .then((response: AxiosResponse<Branch>) => {
          Utils.downloadFile(this.selectedBranch, 'json', response?.data);
        })
        .finally(() => {
          this.isDownloadLoading = false;
        });
    },

    deleteBranch() {
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/configs/${this.selectedBranch}`,
        successMessage,
        failureMessage,
      })
        .then(async () => {
          this.branches = await this.branchesStore.loadBranches();
          await this.branchesStore.setSelectedBranch();
        })
        .finally(() => {
          this.showDeleteBranchModal = false;
        });
    },
  },

  async created() {
    this.branches = await this.branchesStore.getBranches();
  },
});
</script>
